/**
 * Diamond–square heightmap (2^n + 1 grid), per
 * https://en.wikipedia.org/wiki/Diamond-square_algorithm
 *
 * We build a 2D heightmap, then take one horizontal row as a mountain
 * silhouette. Deterministic PRNG so SVG output is stable across renders.
 */

export function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type RidgePoint = readonly [x: number, y: number];

/**
 * Laplacian-style smoothing on elevation only (fixed x) — softens sharp corners
 * so the ridgeline reads more like a treeline / rounded summits, not blur.
 */
export function smoothRidgeY(
  ridge: RidgePoint[],
  passes: number,
  neighborBlend: number
): RidgePoint[] {
  if (ridge.length < 3 || passes < 1) {
    return ridge.slice();
  }
  const w = neighborBlend;
  let pts: [number, number][] = ridge.map(([x, y]) => [x, y]);
  for (let p = 0; p < passes; p++) {
    pts = pts.map((row, i) => {
      if (i === 0 || i === pts.length - 1) {
        return row;
      }
      const yNew = (1 - 2 * w) * row[1] + w * (pts[i - 1][1] + pts[i + 1][1]);
      return [row[0], yNew];
    });
  }
  return pts;
}

/**
 * Closed fill path with cubic segments along the ridge (Catmull–Rom → Bézier),
 * gentler than a polyline of L commands.
 */
export function ridgePathSmoothCurves(
  ridge: RidgePoint[],
  width: number,
  height: number
): string {
  const n = ridge.length;
  if (n < 2) {
    return `M0,${height} L${width},${height} Z`;
  }
  if (n === 2) {
    return `M0,${height} L${ridge[0][0]},${ridge[0][1]} L${ridge[1][0]},${ridge[1][1]} L${width},${height} Z`;
  }

  const parts: string[] = [`M0,${height}`, `L${ridge[0][0]},${ridge[0][1]}`];

  for (let i = 0; i < n - 1; i++) {
    const p0 = ridge[i === 0 ? 0 : i - 1];
    const p1 = ridge[i];
    const p2 = ridge[i + 1];
    const p3 = ridge[i + 2 < n ? i + 2 : n - 1];

    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;

    parts.push(`C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`);
  }

  parts.push(`L${width},${height} Z`);
  return parts.join(" ");
}

export function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/**
 * East→west cross-section inspired by Colorado’s Rockies: eastern foothills ramp,
 * distinct Front Range / Sawatch / Elk–San Juan massifs, then a western taper.
 * t ∈ [0,1] = position along the horizon (east on the left).
 */
export function rockiesElevationEnvelope(t: number): number {
  const foothill = smoothstep(0, 0.22, t) ** 1.15 * 0.28;
  const frontRange =
    Math.exp(-(((t - 0.26) / 0.088) ** 2)) * 0.98;
  const sawatch = Math.exp(-(((t - 0.47) / 0.115) ** 2)) * 1;
  const elkSanJuan = Math.exp(-(((t - 0.69) / 0.092) ** 2)) * 0.78;
  const westFade = 1 - smoothstep(0.8, 0.98, t) * 0.62;
  return clamp01((foothill + frontRange + sawatch + elkSanJuan) * westFade);
}

type FourteenerSpike = { cx: number; width: number; amp: number };

/** Fixed spike positions for one ridgeline (evaluated at each t). */
function buildFourteenerSpikes(
  spikeSeed: number,
  spikeCount: number
): FourteenerSpike[] {
  const rng = mulberry32(spikeSeed);
  const spikes: FourteenerSpike[] = [];
  for (let k = 0; k < spikeCount; k++) {
    spikes.push({
      cx: 0.035 + rng() * 0.91,
      width: 0.007 + rng() * 0.024,
      amp: 0.42 + rng() * 0.62,
    });
  }
  return spikes;
}

function spikeHeightAt(t: number, spikes: FourteenerSpike[]): number {
  let sum = 0;
  for (const s of spikes) {
    sum += s.amp * Math.exp(-(((t - s.cx) / s.width) ** 2));
  }
  return sum;
}

/** Grid side length = 2^exponent + 1 (e.g. exponent 6 → 65×65). */
export function diamondSquareHeightmap(options: {
  exponent: number;
  seed: number;
  roughness: number;
  roughnessDecay: number;
}): number[][] {
  const { exponent, seed, roughness, roughnessDecay } = options;
  const size = 2 ** exponent + 1;
  const max = size - 1;
  const rng = mulberry32(seed);
  const map: number[][] = Array.from({ length: size }, () =>
    Array(size).fill(0)
  );

  map[0][0] = rng();
  map[0][max] = rng();
  map[max][0] = rng();
  map[max][max] = rng();

  let amplitude = roughness;

  for (let step = max; step >= 2; step /= 2) {
    const half = step / 2;

    // Square step (Wikipedia “diamond step”): center of each square = avg(corners) + noise
    for (let y = half; y < max; y += step) {
      for (let x = half; x < max; x += step) {
        const a = map[y - half][x - half];
        const b = map[y - half][x + half];
        const c = map[y + half][x - half];
        const d = map[y + half][x + half];
        map[y][x] = (a + b + c + d) / 4 + (rng() * 2 - 1) * amplitude;
      }
    }

    // Diamond step (Wikipedia “square step”): midpoint of each diamond = avg(neighbors) + noise
    for (let y = 0; y <= max; y += half) {
      for (let x = (y + half) % step; x <= max; x += step) {
        let sum = 0;
        let n = 0;
        if (x >= half) {
          sum += map[y][x - half];
          n++;
        }
        if (x + half <= max) {
          sum += map[y][x + half];
          n++;
        }
        if (y >= half) {
          sum += map[y - half][x];
          n++;
        }
        if (y + half <= max) {
          sum += map[y + half][x];
          n++;
        }
        if (n > 0) {
          map[y][x] = sum / n + (rng() * 2 - 1) * amplitude;
        }
      }
    }

    amplitude *= roughnessDecay;
  }

  return map;
}

type RockiesProfileOptions = {
  /** >1 = sharper, more jagged peaks vs valleys */
  peakSharpness: number;
  /** 0–1 scales the Colorado-style massif envelope */
  envelopeStrength: number;
  /** extra tall narrow peaks */
  spikeIntensity: number;
  /** how many Gaussian spikes */
  spikeCount: number;
  /** high-frequency serration (sawtooth ridgeline) */
  serration: number;
};

const DEFAULT_ROCKIES: RockiesProfileOptions = {
  peakSharpness: 1.38,
  envelopeStrength: 1,
  spikeIntensity: 0.42,
  spikeCount: 8,
  serration: 0.11,
};

/**
 * One horizontal slice of a diamond–square heightmap → SVG ridge polyline (x 0…width).
 * With `rockies`, blends fractal noise with a Colorado Front Range–style envelope,
 * fourteener spikes, and ridgeline serration.
 */
export function diamondSquareRidge(options: {
  width: number;
  exponent: number;
  seed: number;
  roughness: number;
  roughnessDecay: number;
  /** 0 = north row, 1 = south row of the heightmap */
  rowT: number;
  yLeft: number;
  yRight: number;
  peakSpan: number;
  rockies?: Partial<RockiesProfileOptions> | false;
}): RidgePoint[] {
  const {
    width,
    exponent,
    seed,
    roughness,
    roughnessDecay,
    rowT,
    yLeft,
    yRight,
    peakSpan,
    rockies: rockiesPartial,
  } = options;

  const rockies =
    rockiesPartial === false
      ? null
      : { ...DEFAULT_ROCKIES, ...rockiesPartial };

  const map = diamondSquareHeightmap({
    exponent,
    seed,
    roughness,
    roughnessDecay,
  });
  const size = map.length;
  const max = size - 1;
  const rowIndex = Math.round(rowT * max);
  const row = map[rowIndex];

  let minH = Infinity;
  let maxH = -Infinity;
  for (let i = 0; i < size; i++) {
    const v = row[i];
    if (v < minH) minH = v;
    if (v > maxH) maxH = v;
  }
  const span = maxH - minH || 1;

  const spikes = rockies
    ? buildFourteenerSpikes(seed ^ 0x4c6f7269, rockies.spikeCount)
    : null;

  const ridge: RidgePoint[] = [];
  for (let i = 0; i < size; i++) {
    const t = i / max;
    const base = yLeft + t * (yRight - yLeft);
    let norm = (row[i] - minH) / span;

    if (rockies && spikes) {
      const env = rockiesElevationEnvelope(t);
      const blended =
        0.18 * norm +
        0.82 * norm * (1 - rockies.envelopeStrength + rockies.envelopeStrength * env);
      norm = Math.pow(clamp01(blended), rockies.peakSharpness);
      norm += rockies.spikeIntensity * spikeHeightAt(t, spikes);
      norm +=
        rockies.serration *
        env *
        Math.sin((11 + (seed & 7)) * Math.PI * t);
      norm = clamp01(norm);
    }

    const x = t * width;
    const y = base - norm * peakSpan;
    ridge.push([x, y]);
  }

  return ridge;
}
